import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Code2, Type, Eye, Columns, Bold, Italic, Underline,
  List, ListOrdered, AlignLeft,
  AlignCenter, AlignRight, Minus, Sparkles,
  Loader2, Maximize2, Wand2, WrapText, Copy
} from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { Modal } from '@/shared/components/ui/Modal';
import { TEMPLATE_SNIPPETS, type TemplateSnippet } from '../snippets';

export type WorksurfaceMode = 'split' | 'visual' | 'html' | 'preview';

interface TemplateEditorWorksurfaceProps {
  bodyHtml: string;
  onChangeHtml: (html: string) => void;
  previewHtml: string | null;
  onRefreshPreview: () => void;
  isRenderingPreview: boolean;
  /** Override the snippet buttons shown in the editor toolbar. Defaults to HR template snippets. */
  snippets?: TemplateSnippet[];
  forceFullPreview?: boolean;
}

export function TemplateEditorWorksurface({
  bodyHtml,
  onChangeHtml,
  previewHtml,
  onRefreshPreview,
  isRenderingPreview,
  snippets = TEMPLATE_SNIPPETS,
  forceFullPreview = false,
}: TemplateEditorWorksurfaceProps) {
  const [mode, setMode] = useState<WorksurfaceMode>('split');
  const [subMode, setSubMode] = useState<'visual' | 'html'>('visual'); // Which editor to show on left in split view
  const [wordWrap, setWordWrap] = useState(true);
  const [isFullScreenPreview, setIsFullScreenPreview] = useState(false);

  useEffect(() => {
    if (forceFullPreview) {
      setMode('preview');
      if (!previewHtml) onRefreshPreview();
    } else {
      setMode('split');
    }
  }, [forceFullPreview]);

  const htmlRef = useRef<HTMLTextAreaElement>(null);
  const visualRef = useRef<HTMLDivElement>(null);

  // Synchronize visual editor when switching mode or when bodyHtml is replaced
  useEffect(() => {
    if ((mode === 'visual' || (mode === 'split' && subMode === 'visual')) && visualRef.current) {
      if (visualRef.current.innerHTML !== bodyHtml) {
        visualRef.current.innerHTML = bodyHtml;
      }
    }
  }, [mode, subMode, bodyHtml]);

  // Execute formatting commands for Visual Editor
  function formatVisual(command: string, value: string | undefined = undefined) {
    if (!visualRef.current) return;
    visualRef.current.focus();
    document.execCommand(command, false, value);
    onChangeHtml(visualRef.current.innerHTML);
  }

  // Insert snippet HTML at cursor or end
  function insertSnippet(html: string, label: string) {
    if (mode === 'html' || (mode === 'split' && subMode === 'html')) {
      if (htmlRef.current) {
        const el = htmlRef.current;
        const start = el.selectionStart ?? bodyHtml.length;
        const end = el.selectionEnd ?? bodyHtml.length;
        const next = bodyHtml.slice(0, start) + html + bodyHtml.slice(end);
        onChangeHtml(next);
        requestAnimationFrame(() => {
          el.focus();
          const caret = start + html.length;
          el.setSelectionRange(caret, caret);
        });
      } else {
        onChangeHtml(bodyHtml + html);
      }
    } else {
      if (visualRef.current) {
        visualRef.current.focus();
        // Modern fallback for inserting HTML
        const success = document.execCommand('insertHTML', false, html);
        if (!success) {
          visualRef.current.innerHTML += html;
        }
        onChangeHtml(visualRef.current.innerHTML);
      } else {
        onChangeHtml(bodyHtml + html);
      }
    }
    toast.success(`Inserted ${label}`);
  }

  // Format HTML indentation
  function formatHtmlCode() {
    try {
      const formatted = bodyHtml
        .replace(/></g, '>\n<')
        .replace(/^\s+|\s+$/g, '');
      onChangeHtml(formatted);
      toast.success('Formatted HTML markup');
    } catch {
      toast.error('Could not format HTML');
    }
  }

  function handleCopyHtml() {
    navigator.clipboard.writeText(bodyHtml);
    toast.success('Copied full HTML template to clipboard');
  }

  const wordCount = bodyHtml.replace(/<[^>]*>?/gm, ' ').trim().split(/\s+/).filter(Boolean).length;
  const lineCount = bodyHtml.split('\n').length;

  return (
    <div className="space-y-4">
      {/* Worksurface Tabs Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-outline bg-surface p-2 shadow-sm">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setMode('split')}
            className={`flex items-center gap-2 rounded-lg px-3.5 py-1.5 text-xs font-semibold transition-all ${
              mode === 'split'
                ? 'bg-primary text-on-primary shadow-xs'
                : 'text-on-surface-variant hover:bg-surface-variant'
            }`}
          >
            <Columns className="h-4 w-4" />
            <span>Split View (Side by Side)</span>
          </button>

          <button
            type="button"
            onClick={() => setMode('visual')}
            className={`flex items-center gap-2 rounded-lg px-3.5 py-1.5 text-xs font-semibold transition-all ${
              mode === 'visual'
                ? 'bg-primary text-on-primary shadow-xs'
                : 'text-on-surface-variant hover:bg-surface-variant'
            }`}
          >
            <Type className="h-4 w-4" />
            <span>Visual Editor</span>
          </button>

          <button
            type="button"
            onClick={() => setMode('html')}
            className={`flex items-center gap-2 rounded-lg px-3.5 py-1.5 text-xs font-semibold transition-all ${
              mode === 'html'
                ? 'bg-primary text-on-primary shadow-xs'
                : 'text-on-surface-variant hover:bg-surface-variant'
            }`}
          >
            <Code2 className="h-4 w-4" />
            <span>HTML Code</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setMode('preview');
              if (!previewHtml) onRefreshPreview();
            }}
            className={`flex items-center gap-2 rounded-lg px-3.5 py-1.5 text-xs font-semibold transition-all ${
              mode === 'preview'
                ? 'bg-primary text-on-primary shadow-xs'
                : 'text-on-surface-variant hover:bg-surface-variant'
            }`}
          >
            <Eye className="h-4 w-4" />
            <span>Live Letterhead Preview</span>
          </button>
        </div>

        {/* Quick action controls on right */}
        <div className="flex items-center gap-2">
          {mode === 'split' && (
            <div className="flex items-center rounded-lg border border-outline bg-surface-variant/40 p-0.5">
              <button
                type="button"
                onClick={() => setSubMode('visual')}
                className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                  subMode === 'visual' ? 'bg-surface text-primary shadow-xs' : 'text-muted-foreground'
                }`}
              >
                Visual Left
              </button>
              <button
                type="button"
                onClick={() => setSubMode('html')}
                className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                  subMode === 'html' ? 'bg-surface text-primary shadow-xs' : 'text-muted-foreground'
                }`}
              >
                HTML Left
              </button>
            </div>
          )}

          <Button
            size="sm"
            variant="outline"
            onClick={onRefreshPreview}
            loading={isRenderingPreview}
            className="text-xs bg-surface shadow-xs"
            title="Render preview with sample candidate & salary data"
          >
            <Eye className="h-3.5 w-3.5 mr-1.5 text-primary" />
            Refresh Preview
          </Button>

          {previewHtml && (mode === 'preview' || mode === 'split') && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setIsFullScreenPreview(true)}
              className="text-xs text-muted-foreground hover:text-on-surface"
              title="View letterhead full screen"
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Main Mode Containers */}
      {mode === 'split' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Left: Active Editor */}
          <Card className="flex flex-col shadow-sm border-outline">
            <CardHeader
              title={subMode === 'visual' ? 'Visual Document Editor' : 'HTML Code Source'}
              subtitle="Edit template body"
            />
            {subMode === 'visual' ? (
              <VisualEditorContainer
                visualRef={visualRef}
                onChangeHtml={onChangeHtml}
                onFormat={formatVisual}
                onInsertSnippet={insertSnippet}
                snippets={snippets}
              />
            ) : (
              <HtmlEditorContainer
                htmlRef={htmlRef}
                bodyHtml={bodyHtml}
                onChangeHtml={onChangeHtml}
                wordWrap={wordWrap}
                onToggleWrap={() => setWordWrap(!wordWrap)}
                onFormatCode={formatHtmlCode}
                onCopy={handleCopyHtml}
                onInsertSnippet={insertSnippet}
                wordCount={wordCount}
                lineCount={lineCount}
                snippets={snippets}
              />
            )}
          </Card>

          {/* Right: Live Preview */}
          <Card className="flex flex-col shadow-sm border-outline">
            <CardHeader
              title="Live Document Preview"
              subtitle="Rendered with HR Branding & sample data"
              action={
                <span className="inline-flex items-center gap-1.5 rounded-full bg-success/10 px-2.5 py-0.5 text-[11px] font-medium text-success-text">
                  <span className="h-1.5 w-1.5 rounded-full bg-success" /> Letterhead Active
                </span>
              }
            />
            <CardBody className="p-0 flex-1 min-h-[780px] lg:min-h-[calc(100vh-220px)] relative bg-slate-100/80 rounded-b-xl overflow-hidden">
              {isRenderingPreview ? (
                <div className="flex flex-col items-center justify-center min-h-[780px] text-muted-foreground">
                  <Loader2 className="h-7 w-7 animate-spin text-primary mb-2" />
                  <p className="text-xs font-medium">Generating preview with company letterhead...</p>
                </div>
              ) : previewHtml ? (
                <iframe
                  title="Live Split Preview"
                  srcDoc={previewHtml}
                  className="w-full min-h-[780px] lg:h-[calc(100vh-220px)] border-0 bg-white shadow-inner"
                />
              ) : (
                <div className="flex flex-col items-center justify-center min-h-[780px] p-6 text-center">
                  <Eye className="h-8 w-8 text-muted-foreground/50 mb-2" />
                  <p className="text-sm font-semibold text-on-surface">Preview Not Rendered</p>
                  <p className="text-xs text-muted-foreground max-w-sm mt-1">
                    Click <strong>Refresh Preview</strong> above to render this template with sample employee and salary data.
                  </p>
                  <Button size="sm" variant="outline" onClick={onRefreshPreview} className="mt-4">
                    Render Live Preview
                  </Button>
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      )}

      {mode === 'visual' && (
        <Card className="shadow-sm border-outline">
          <CardHeader title="Visual Document Editor" subtitle="WYSIWYG authoring with company letterhead integration" />
          <VisualEditorContainer
            visualRef={visualRef}
            onChangeHtml={onChangeHtml}
            onFormat={formatVisual}
            onInsertSnippet={insertSnippet}
            snippets={snippets}
            minHeight="min-h-[680px]"
          />
        </Card>
      )}

      {mode === 'html' && (
        <Card className="shadow-sm border-outline">
          <CardHeader title="HTML Template Source" subtitle="Handlebars-compatible HTML code" />
          <HtmlEditorContainer
            htmlRef={htmlRef}
            bodyHtml={bodyHtml}
            onChangeHtml={onChangeHtml}
            wordWrap={wordWrap}
            onToggleWrap={() => setWordWrap(!wordWrap)}
            onFormatCode={formatHtmlCode}
            onCopy={handleCopyHtml}
            onInsertSnippet={insertSnippet}
            wordCount={wordCount}
            lineCount={lineCount}
            snippets={snippets}
            minHeight="min-h-[680px]"
          />
        </Card>
      )}

      {mode === 'preview' && (
        <Card className="shadow-sm border-outline">
          <CardHeader
            title="Full Page Live Preview"
            subtitle="Letterhead + legal identity + sample data"
            action={
              <Button size="sm" variant="outline" onClick={onRefreshPreview} loading={isRenderingPreview}>
                <Eye className="h-3.5 w-3.5 mr-1.5" />
                Refresh Sample Data
              </Button>
            }
          />
          <CardBody className="p-0 bg-slate-100/80 rounded-b-xl overflow-hidden min-h-[850px]">
            {isRenderingPreview ? (
              <div className="flex flex-col items-center justify-center min-h-[850px] text-muted-foreground">
                <Loader2 className="h-7 w-7 animate-spin text-primary mb-2" />
                <p className="text-xs font-medium">Generating preview...</p>
              </div>
            ) : previewHtml ? (
              <iframe
                title="Full Page Preview"
                srcDoc={previewHtml}
                className="w-full min-h-[850px] lg:h-[calc(100vh-200px)] border-0 bg-white shadow-md"
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-[680px] p-6 text-center">
                <Eye className="h-8 w-8 text-muted-foreground/50 mb-2" />
                <p className="text-sm font-semibold text-on-surface">Preview Not Rendered</p>
                <Button size="sm" variant="primary" onClick={onRefreshPreview} className="mt-4">
                  Render Now
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Full screen modal for preview zoom */}
      <Modal
        open={isFullScreenPreview}
        onClose={() => setIsFullScreenPreview(false)}
        title="Full Screen Document Preview"
        size="lg"
      >
        <div className="h-[75vh] w-full bg-slate-100 p-2 rounded-lg overflow-hidden">
          {previewHtml ? (
            <iframe
              title="Fullscreen preview"
              srcDoc={previewHtml}
              className="w-full h-full rounded-md border-0 bg-white shadow-xl"
            />
          ) : (
            <div className="flex items-center justify-center h-full">No preview available</div>
          )}
        </div>
      </Modal>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Visual WYSIWYG Editor Container
───────────────────────────────────────────────────────────────────────────── */
function VisualEditorContainer({
  visualRef,
  onChangeHtml,
  onFormat,
  onInsertSnippet,
  snippets,
  minHeight = 'min-h-[580px]',
}: {
  visualRef: React.RefObject<HTMLDivElement | null>;
  onChangeHtml: (html: string) => void;
  onFormat: (cmd: string, val?: string) => void;
  onInsertSnippet: (html: string, label: string) => void;
  snippets: TemplateSnippet[];
  minHeight?: string;
}) {
  return (
    <div className="flex flex-col border-t border-outline/50 bg-surface">
      {/* Formatting Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 bg-surface-variant/40 border-b border-outline/50">
        <div className="flex items-center gap-0.5 border-r border-outline/40 pr-1">
          <button
            type="button"
            onClick={() => onFormat('bold')}
            className="p-1.5 rounded hover:bg-surface text-on-surface-variant hover:text-primary"
            title="Bold (⌘B)"
          >
            <Bold className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onFormat('italic')}
            className="p-1.5 rounded hover:bg-surface text-on-surface-variant hover:text-primary"
            title="Italic (⌘I)"
          >
            <Italic className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onFormat('underline')}
            className="p-1.5 rounded hover:bg-surface text-on-surface-variant hover:text-primary"
            title="Underline (⌘U)"
          >
            <Underline className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="flex items-center gap-0.5 border-r border-outline/40 pr-1">
          <button
            type="button"
            onClick={() => onFormat('formatBlock', '<h1>')}
            className="px-2 py-1 rounded hover:bg-surface text-[11px] font-bold text-on-surface-variant hover:text-primary"
            title="Heading 1"
          >
            H1
          </button>
          <button
            type="button"
            onClick={() => onFormat('formatBlock', '<h2>')}
            className="px-2 py-1 rounded hover:bg-surface text-[11px] font-bold text-on-surface-variant hover:text-primary"
            title="Heading 2"
          >
            H2
          </button>
          <button
            type="button"
            onClick={() => onFormat('formatBlock', '<h3>')}
            className="px-2 py-1 rounded hover:bg-surface text-[11px] font-bold text-on-surface-variant hover:text-primary"
            title="Heading 3"
          >
            H3
          </button>
          <button
            type="button"
            onClick={() => onFormat('formatBlock', '<p>')}
            className="px-2 py-1 rounded hover:bg-surface text-[11px] font-medium text-on-surface-variant hover:text-primary"
            title="Normal Paragraph"
          >
            P
          </button>
        </div>

        <div className="flex items-center gap-0.5 border-r border-outline/40 pr-1">
          <button
            type="button"
            onClick={() => onFormat('insertUnorderedList')}
            className="p-1.5 rounded hover:bg-surface text-on-surface-variant hover:text-primary"
            title="Bullet List"
          >
            <List className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onFormat('insertOrderedList')}
            className="p-1.5 rounded hover:bg-surface text-on-surface-variant hover:text-primary"
            title="Numbered List"
          >
            <ListOrdered className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="flex items-center gap-0.5 border-r border-outline/40 pr-1">
          <button
            type="button"
            onClick={() => onFormat('justifyLeft')}
            className="p-1.5 rounded hover:bg-surface text-on-surface-variant hover:text-primary"
            title="Align Left"
          >
            <AlignLeft className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onFormat('justifyCenter')}
            className="p-1.5 rounded hover:bg-surface text-on-surface-variant hover:text-primary"
            title="Align Center"
          >
            <AlignCenter className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onFormat('justifyRight')}
            className="p-1.5 rounded hover:bg-surface text-on-surface-variant hover:text-primary"
            title="Align Right"
          >
            <AlignRight className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Snippet Insertions */}
        <div className="flex items-center gap-1.5 ml-auto">
          {snippets.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => onInsertSnippet(s.html, s.label)}
              className="inline-flex items-center gap-1 rounded border border-outline bg-surface px-2 py-1 text-[11px] font-medium text-on-surface hover:border-primary hover:text-primary transition-colors"
              title={s.description}
            >
              <Sparkles className="h-3 w-3 text-primary" />
              <span>+{s.label}</span>
            </button>
          ))}

          <button
            type="button"
            onClick={() => onFormat('insertHorizontalRule')}
            className="inline-flex items-center gap-1 rounded border border-outline bg-surface px-2 py-1 text-[11px] font-medium text-on-surface hover:border-primary hover:text-primary"
            title="Insert Horizontal Divider Rule"
          >
            <Minus className="h-3 w-3" />
            <span>Divider</span>
          </button>
        </div>
      </div>

      {/* Styled Paper Sheet Area */}
      <div className="p-6 bg-slate-100/70 overflow-auto">
        <div
          ref={visualRef}
          contentEditable
          suppressContentEditableWarning
          onInput={(e) => onChangeHtml(e.currentTarget.innerHTML)}
          className={`w-full max-w-4xl mx-auto ${minHeight} rounded-lg border border-outline/80 bg-white p-8 text-sm text-gray-900 shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40`}
          style={{ fontFamily: 'Inter, system-ui, sans-serif', lineHeight: '1.6' }}
          aria-label="Template visual editor"
        />
      </div>

      <div className="px-4 py-2 bg-surface-variant/30 border-t border-outline/40 flex items-center justify-between text-[11px] text-muted-foreground">
        <span>💡 Authored HTML is automatically sanitized on save (scripts and event handlers are removed).</span>
        <span>WYSIWYG Visual Mode</span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   HTML Source Code Editor Container
───────────────────────────────────────────────────────────────────────────── */
function HtmlEditorContainer({
  htmlRef,
  bodyHtml,
  onChangeHtml,
  wordWrap,
  onToggleWrap,
  onFormatCode,
  onCopy,
  onInsertSnippet,
  snippets,
  wordCount,
  lineCount,
  minHeight = 'h-[580px]',
}: {
  htmlRef: React.RefObject<HTMLTextAreaElement | null>;
  bodyHtml: string;
  onChangeHtml: (html: string) => void;
  wordWrap: boolean;
  onToggleWrap: () => void;
  onFormatCode: () => void;
  onCopy: () => void;
  onInsertSnippet: (html: string, label: string) => void;
  snippets: TemplateSnippet[];
  wordCount: number;
  lineCount: number;
  minHeight?: string;
}) {
  return (
    <div className="flex flex-col border-t border-outline/50 bg-surface">
      {/* HTML Utility Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2 p-2 bg-surface-variant/40 border-b border-outline/50">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onToggleWrap}
            className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium border transition-colors ${
              wordWrap
                ? 'bg-primary/10 border-primary/40 text-primary'
                : 'bg-surface border-outline/60 text-muted-foreground hover:text-on-surface'
            }`}
          >
            <WrapText className="h-3.5 w-3.5" />
            <span>Word Wrap: {wordWrap ? 'On' : 'Off'}</span>
          </button>

          <button
            type="button"
            onClick={onFormatCode}
            className="inline-flex items-center gap-1.5 rounded border border-outline bg-surface px-2.5 py-1 text-xs font-medium text-on-surface hover:border-primary hover:text-primary transition-colors"
            title="Clean linebreaks and format HTML markup"
          >
            <Wand2 className="h-3.5 w-3.5 text-primary" />
            <span>Format Code</span>
          </button>

          <button
            type="button"
            onClick={onCopy}
            className="inline-flex items-center gap-1.5 rounded border border-outline bg-surface px-2.5 py-1 text-xs font-medium text-on-surface hover:border-primary hover:text-primary transition-colors"
          >
            <Copy className="h-3.5 w-3.5" />
            <span>Copy HTML</span>
          </button>
        </div>

        {/* Snippet Insertions */}
        {snippets.length > 0 && (
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-muted-foreground mr-1">Snippets:</span>
            {snippets.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => onInsertSnippet(s.html, s.label)}
                className="inline-flex items-center gap-1 rounded border border-outline bg-surface px-2 py-1 text-[11px] font-medium text-on-surface hover:border-primary hover:text-primary transition-colors"
                title={s.description}
              >
                <span>+{s.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Monospace Code Textarea */}
      <div className="relative bg-[#0d1117] p-4 overflow-hidden">
        <textarea
          ref={htmlRef}
          value={bodyHtml}
          onChange={(e) => onChangeHtml(e.target.value)}
          spellCheck={false}
          wrap={wordWrap ? 'soft' : 'off'}
          className={`w-full ${minHeight} rounded-md border border-slate-700 bg-transparent p-3 font-mono text-xs text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40 leading-relaxed`}
          aria-label="Template HTML source"
          style={{ tabSize: 2 }}
        />
      </div>

      {/* Code Status Bar Footer */}
      <div className="px-4 py-2 bg-surface-variant/30 border-t border-outline/40 flex items-center justify-between text-[11px] text-muted-foreground">
        <div className="flex items-center gap-4">
          <span>
            <strong>{lineCount}</strong> lines
          </span>
          <span>
            <strong>{wordCount}</strong> words
          </span>
          <span>
            <strong>{bodyHtml.length}</strong> characters
          </span>
        </div>
        <span className="font-mono text-xs">HTML / Handlebars</span>
      </div>
    </div>
  );
}
